import { useForm } from "@inertiajs/react"
import { useState, useCallback, type FormEvent } from "react"
import { track } from "@/lib/analytics"
import type { Listing } from "@/types"
import ImageLightbox from "@/components/dashboard/image-lightbox"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { OptimizedImage } from "@/components/ui/optimized-image"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type ListingFormData = {
  address: string
  price: string
  bedrooms: string
  bathrooms: string
  property_type: string
  listing_type: string
  status: string
  description: string
  features: string[]
  source_url: string
  nightly_rate: string
  min_nights: string
  availability_url: string
  images: File[]
  remove_image_ids: string[]
  image_descriptions: Record<string, string>
}

const LISTING_TYPES = [
  { value: "sale", label: "For sale" },
  { value: "long_let", label: "Long let" },
  { value: "short_let", label: "Short let" },
  { value: "holiday_let", label: "Holiday let" },
]

const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
]

const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "flat", label: "Flat" },
  { value: "apartment", label: "Apartment" },
  { value: "bungalow", label: "Bungalow" },
  { value: "cottage", label: "Cottage" },
  { value: "studio", label: "Studio" },
  { value: "detached", label: "Detached" },
  { value: "semi_detached", label: "Semi-detached" },
  { value: "terraced", label: "Terraced" },
  { value: "maisonette", label: "Maisonette" },
  { value: "land", label: "Land" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" },
]

export default function ListingForm({
  listing,
  action,
}: {
  listing?: Listing
  action: "create" | "update"
}) {
  const isEditing = action === "update"
  const [featureInput, setFeatureInput] = useState("")

  const { data, setData, post, patch, processing, errors, transform } = useForm<ListingFormData>({
    address: listing?.address || "",
    price: listing?.price || "",
    bedrooms: listing?.bedrooms?.toString() || "",
    bathrooms: listing?.bathrooms?.toString() || "",
    property_type: listing?.property_type || "",
    listing_type: listing?.listing_type || "sale",
    status: listing?.status || "active",
    description: listing?.description || "",
    features: listing?.features || [],
    source_url: listing?.source_url || "",
    nightly_rate: listing?.nightly_rate?.toString() || "",
    min_nights: listing?.min_nights?.toString() || "",
    availability_url: listing?.availability_url || "",
    images: [],
    remove_image_ids: [],
    image_descriptions: listing?.images_data?.reduce((acc, img) => {
      if (img.description) acc[img.signed_id] = img.description
      return acc
    }, {} as Record<string, string>) || {},
  })

  transform((d) => {
    const formData = new FormData()
    formData.append("listing[address]", d.address)
    formData.append("listing[price]", d.price)
    if (d.bedrooms) formData.append("listing[bedrooms]", d.bedrooms)
    if (d.bathrooms) formData.append("listing[bathrooms]", d.bathrooms)
    if (d.property_type) formData.append("listing[property_type]", d.property_type)
    formData.append("listing[listing_type]", d.listing_type)
    formData.append("listing[status]", d.status)
    formData.append("listing[description]", d.description)
    d.features.forEach((f) => formData.append("listing[features][]", f))
    formData.append("listing[source_url]", d.source_url)
    if (d.nightly_rate) formData.append("listing[nightly_rate]", d.nightly_rate)
    if (d.min_nights) formData.append("listing[min_nights]", d.min_nights)
    if (d.availability_url) formData.append("listing[availability_url]", d.availability_url)
    d.images.forEach((img) => formData.append("listing[images][]", img))
    d.remove_image_ids.forEach((id) => formData.append("listing[remove_image_ids][]", id))
    Object.entries(d.image_descriptions).forEach(([signedId, desc]) => {
      formData.append(`listing[image_descriptions][${signedId}]`, desc)
    })
    return formData as unknown as ListingFormData
  })

  const isShortLet = data.listing_type === "short_let" || data.listing_type === "holiday_let"

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    track(isEditing ? "listing_updated" : "listing_created", {
      listing_type: data.listing_type,
      property_type: data.property_type,
    })
    if (isEditing && listing) {
      patch(`/dashboard/listings/${listing.slug}`, { forceFormData: true })
    } else {
      post("/dashboard/listings", { forceFormData: true })
    }
  }

  const addFeature = useCallback(() => {
    const trimmed = featureInput.trim()
    if (trimmed && !data.features.includes(trimmed)) {
      setData("features", [...data.features, trimmed])
      setFeatureInput("")
    }
  }, [featureInput, data.features, setData])

  const removeFeature = (feature: string) => {
    setData("features", data.features.filter((f) => f !== feature))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Property details */}
      <section className="rounded-xl border border-stone/40 bg-white p-6">
        <h2 className="mb-6 font-display text-lg font-semibold text-charcoal">
          Property details
        </h2>
        <div className="space-y-4">
          <div>
            <Label htmlFor="address">Address *</Label>
            <Input
              id="address"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              placeholder="42 Queen Street, Edinburgh, EH2 3NH"
              className="mt-1.5"
              required
            />
            {errors.address && <p className="mt-1 text-xs text-red">{errors.address}</p>}
          </div>

          <div className={`grid gap-4 ${isEditing ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            <div>
              <Label htmlFor="listing_type">Listing type *</Label>
              <Select value={data.listing_type} onValueChange={(v) => setData("listing_type", v)}>
                <SelectTrigger id="listing_type" className="mt-1.5">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {LISTING_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.listing_type && <p className="mt-1 text-xs text-red">{errors.listing_type}</p>}
            </div>

            <div>
              <Label htmlFor="property_type">Property type</Label>
              <Select value={data.property_type} onValueChange={(v) => setData("property_type", v)}>
                <SelectTrigger id="property_type" className="mt-1.5">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isEditing && (
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={data.status} onValueChange={(v) => setData("status", v)}>
                  <SelectTrigger id="status" className="mt-1.5">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                value={data.price}
                onChange={(e) => setData("price", e.target.value)}
                placeholder="£475,000"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input
                id="bedrooms"
                type="number"
                min={0}
                max={20}
                value={data.bedrooms}
                onChange={(e) => setData("bedrooms", e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="bathrooms">Bathrooms</Label>
              <Input
                id="bathrooms"
                type="number"
                min={0}
                max={20}
                value={data.bathrooms}
                onChange={(e) => setData("bathrooms", e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Description & features */}
      <section className="rounded-xl border border-stone/40 bg-white p-6">
        <h2 className="mb-6 font-display text-lg font-semibold text-charcoal">
          Description & features
        </h2>
        <div className="space-y-4">
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={data.description}
              onChange={(e) => setData("description", e.target.value)}
              placeholder="Property description, markdown supported (headings, bullet points, bold)"
              rows={24}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="feature_input">Features</Label>
            <div className="mt-1.5 flex gap-2">
              <Input
                id="feature_input"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    addFeature()
                  }
                }}
                placeholder="Type a feature and press Enter"
              />
              <Button type="button" variant="outline" onClick={addFeature}>
                Add
              </Button>
            </div>
            {data.features.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {data.features.map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-1 rounded-full bg-stone/30 px-3 py-1 text-xs font-medium text-charcoal"
                  >
                    {feature}
                    <button
                      type="button"
                      onClick={() => removeFeature(feature)}
                      className="ml-0.5 text-warm-grey hover:text-red"
                    >
                      <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Short-let details */}
      {isShortLet && (
        <section className="rounded-xl border border-stone/40 bg-white p-6">
          <h2 className="mb-6 font-display text-lg font-semibold text-charcoal">
            Short-let details
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="nightly_rate">Nightly rate</Label>
              <Input
                id="nightly_rate"
                type="number"
                min={0}
                step="0.01"
                value={data.nightly_rate}
                onChange={(e) => setData("nightly_rate", e.target.value)}
                placeholder="£150"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="min_nights">Minimum nights</Label>
              <Input
                id="min_nights"
                type="number"
                min={1}
                value={data.min_nights}
                onChange={(e) => setData("min_nights", e.target.value)}
                placeholder="2"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="availability_url">Availability URL</Label>
              <Input
                id="availability_url"
                type="url"
                value={data.availability_url}
                onChange={(e) => setData("availability_url", e.target.value)}
                placeholder="https://airbnb.co.uk/..."
                className="mt-1.5"
              />
            </div>
          </div>
        </section>
      )}

      {/* Images */}
      <section className="rounded-xl border border-stone/40 bg-white p-6">
        <h2 className="mb-6 font-display text-lg font-semibold text-charcoal">
          Images
        </h2>
        <ImageUpload
          images={data.images}
          existingImages={listing?.images_data || []}
          removeImageIds={data.remove_image_ids}
          imageDescriptions={data.image_descriptions}
          onImagesChange={(files) => setData("images", files)}
          onRemoveExisting={(id) =>
            setData("remove_image_ids", [...data.remove_image_ids, id])
          }
          onRestoreExisting={(id) =>
            setData("remove_image_ids", data.remove_image_ids.filter((i) => i !== id))
          }
          onDescriptionChange={(signedId, desc) =>
            setData("image_descriptions", { ...data.image_descriptions, [signedId]: desc })
          }
        />
      </section>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3">
        <Button type="submit" disabled={processing} className="bg-red text-white hover:bg-red-deep">
          {processing ? "Saving..." : isEditing ? "Update listing" : "Create listing"}
        </Button>
      </div>
    </form>
  )
}

function ImageUpload({
  images,
  existingImages,
  removeImageIds,
  imageDescriptions,
  onImagesChange,
  onRemoveExisting,
  onRestoreExisting,
  onDescriptionChange,
}: {
  images: File[]
  existingImages: Array<{ signed_id: string; url: string; thumb_url: string; description?: string }>
  removeImageIds: string[]
  imageDescriptions: Record<string, string>
  onImagesChange: (files: File[]) => void
  onRemoveExisting: (signedId: string) => void
  onRestoreExisting: (signedId: string) => void
  onDescriptionChange: (signedId: string, description: string) => void
}) {
  const [previews, setPreviews] = useState<string[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return
    const newFiles = Array.from(files).filter((f) => f.type.startsWith("image/"))
    onImagesChange([...images, ...newFiles])

    const newPreviews = newFiles.map((f) => URL.createObjectURL(f))
    setPreviews((prev) => [...prev, ...newPreviews])
  }, [images, onImagesChange])

  const removeNew = (index: number) => {
    onImagesChange(images.filter((_, i) => i !== index))
    URL.revokeObjectURL(previews[index])
    setPreviews(previews.filter((_, i) => i !== index))
  }

  const keptImages = existingImages.filter((img) => !removeImageIds.includes(img.signed_id))
  const removedImages = existingImages.filter((img) => removeImageIds.includes(img.signed_id))

  return (
    <div>
      {/* Existing images */}
      {keptImages.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium text-warm-grey">
            Current images
            <span className="ml-1 text-warm-grey/50">, click to preview</span>
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {keptImages.map((img, i) => (
              <div key={img.signed_id} className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-stone/40"
                >
                  <OptimizedImage src={img.thumb_url} alt="" className="size-full object-cover transition-transform duration-200 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
                <textarea
                  value={imageDescriptions[img.signed_id] || ""}
                  onChange={(e) => onDescriptionChange(img.signed_id, e.target.value)}
                  placeholder="Photo description..."
                  rows={2}
                  className="w-full resize-none rounded border border-stone/30 bg-cream/30 px-2 py-1.5 text-xs leading-relaxed text-slate placeholder:text-warm-grey/50 focus:border-stone/50 focus:outline-none focus:ring-1 focus:ring-stone/20"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {lightboxIndex !== null && keptImages.length > 0 && (
        <ImageLightbox
          images={keptImages}
          initialIndex={lightboxIndex}
          canDelete
          onDelete={onRemoveExisting}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Removed images (can restore) */}
      {removedImages.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium text-warm-grey">Removed (click to restore)</p>
          <div className="flex flex-wrap gap-2">
            {removedImages.map((img) => (
              <button
                key={img.signed_id}
                type="button"
                onClick={() => onRestoreExisting(img.signed_id)}
                className="relative size-20 overflow-hidden rounded-lg border border-dashed border-stone/40 opacity-40 transition-opacity hover:opacity-70"
              >
                <OptimizedImage src={img.thumb_url} alt="" className="size-full object-cover grayscale" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Drop zone */}
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
          dragOver ? "border-red/40 bg-red/5" : "border-stone/40 hover:border-stone/60"
        }`}
      >
        <svg className="mb-2 size-8 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
        </svg>
        <p className="text-sm text-warm-grey">
          Drop images here or <span className="text-red">browse</span>
        </p>
        <p className="mt-1 text-xs text-warm-grey/70">JPG, PNG, WebP</p>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
      </label>

      {/* New image previews */}
      {previews.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium text-warm-grey">New images</p>
          <div className="flex flex-wrap gap-2">
            {previews.map((url, i) => (
              <div key={i} className="group relative size-20 overflow-hidden rounded-lg border border-stone/40">
                <OptimizedImage src={url} alt="" className="size-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeNew(i)}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <svg className="size-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
